import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Collapse,
  Divider,
  Grid,
  MenuItem,
  TextField
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowDropDown, ArrowDropUp, Search } from "@mui/icons-material";
import {
  fetchShopTypes,
  fetchValidateLocations
} from "../../redux/actions/adminConsole/validate/validateActions";
import { PropTypes } from "prop-types";
import { useTranslation } from "react-i18next";
import { enterKeyCode, validateStatuses } from "../../constants";
import { InputAdornment } from "@mui/material";
import { DateRange } from "@mui/icons-material";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import usePermissions from "../../redux/selectors/usePermissions";
import { isValidDate } from "../../utils/datetime";
import useUser from "../../hooks/useUser";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

const initialFiltersState = {
  statusValue: "",
  shopType: "",
  createdAfter: null,
  createdBefore: null,
  programRangeStartedAfter: null,
  programRangeStartedBefore: null,
  location: "",
  searchInput: ""
};

const useStyles = makeStyles(theme => ({
  filterContainer: {
    color: theme.palette.text.secondary
  }
}));

const FilterDatePickerField = ({
  name,
  value,
  maxDateField,
  maxDate,
  minDateField,
  minDate,
  addError,
  onChange,
  filterErrors,
  setFilterErrors,
  classes,
  ...other
}) => {
  const dateError = filterErrors.includes(name);
  const [currentErrorText, setCurrentErrorText] = useState("");
  const minPossibleDate = new Date("1900-01-01");
  const maxPossibleDate = new Date("2100-01-01");

  const { t } = useTranslation();

  const setError = text => {
    setCurrentErrorText(text);
    if (filterErrors.indexOf(name) === -1) {
      setFilterErrors(filterErrors.concat(name));
    }
  };

  useEffect(() => {
    if (Boolean(currentErrorText)) {
      if (isValidDate(minDate) && isValidDate(value)) {
        if (
          minDate.getTime() <= value.getTime() &&
          minDate <= maxPossibleDate &&
          minDate >= minPossibleDate
        ) {
          setCurrentErrorText("");
        }
      }
    }
  }, [minDate]);

  useEffect(() => {
    if (Boolean(currentErrorText)) {
      if (isValidDate(maxDate) && isValidDate(value)) {
        if (
          maxDate.getTime() >= value.getTime() &&
          maxDate <= maxPossibleDate &&
          maxDate >= minPossibleDate
        ) {
          setCurrentErrorText("");
        }
      }
    }
  }, [maxDate]);

  useEffect(() => {
    if (!dateError) {
      setCurrentErrorText("");
    }
  }, [dateError]);

  const clearError = () => {
    setFilterErrors(filterErrors.filter(word => word !== name));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DatePicker
        clearable
        name={name}
        maxDate={
          maxDate && isValidDate(maxDate) ? maxDate : new Date("2100-01-01")
        }
        minDate={
          minDate && isValidDate(minDate) ? minDate : new Date("1900-01-01")
        }
        value={value}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <DateRange />
            </InputAdornment>
          )
        }}
        onChange={date => {
          if (date) {
            let errorMsg = "";
            if (!isValidDate(date)) {
              setError(t("validationMessages.validDate"));
            } else {
              if (minDate && date < minDate) {
                errorMsg = t("validate.minDateFilterError") + minDateField;
                setError(errorMsg);
              } else if (maxDate && date > maxDate) {
                errorMsg = t("validate.maxDateFilterError") + maxDateField;
                setError(errorMsg);
              } else if (date > maxPossibleDate) {
                errorMsg =
                  t("validate.maxDateFilterError") +
                  maxPossibleDate.toDateString();
                setError(errorMsg);
              } else if (date < minPossibleDate) {
                errorMsg =
                  t("validate.minDateFilterError") +
                  minPossibleDate.toDateString();
                setError(errorMsg);
              } else {
                clearError();
              }
            }
          } else {
            clearError();
          }
          onChange(date);
        }}
        fullWidth
        inputVariant="filled"
        renderInput={params => (
          <TextField
            variant="filled"
            fullWidth
            helperText={currentErrorText}
            error={Boolean(currentErrorText)}
            {...params}
          />
        )}
        {...other}
      />
    </LocalizationProvider>
  );
};

const ExpandableFilterMenu = ({
  expanded,
  statusValue,
  statusOptions,
  onStatusChange,
  location,
  onValidateLocationChange,
  validateLocationOptions,
  shopType,
  createdAfter,
  onCreatedAfterChange,
  createdBefore,
  onCreatedBeforeChange,
  programRangeStartedAfter,
  onProgramRangeStartedAfter,
  programRangeStartedBefore,
  onProgramRangeStartedBefore,
  onShopTypeChange,
  shopTypeOptions,
  onFilterList,
  onClearFilters,
  filterErrors,
  setFilterErrors,
  createdAtFilterDisabled,
  programRangeFilterDisabled
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Collapse in={expanded}>
      <Grid
        container
        spacing={2}
        sx={{ mb: 1 }}
        className={classes.filterContainer}
      >
        <Grid item xs={12} md={4}>
          <TextField
            select
            value={statusValue}
            onChange={onStatusChange}
            variant="filled"
            InputProps={{
              disableUnderline: true,
              inputProps: { "data-qa": "validate-status-filter" }
            }}
            fullWidth
            SelectProps={{
              displayEmpty: true,
              renderValue: value =>
                `${t("validate.validateStatusFilter")}: ${
                  value && statusOptions
                    ? t(
                        statusOptions.find(option => option.value === value)
                          .label
                      )
                    : t("validate.validateFilterShowAllOption")
                }`
            }}
            hiddenLabel
          >
            <MenuItem value="" key="all">
              {t("validate.validateFilterShowAllOption")}
            </MenuItem>
            {!!statusOptions &&
              statusOptions.map(option => (
                <MenuItem value={option.value} key={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            value={location}
            onChange={onValidateLocationChange}
            variant="filled"
            InputProps={{
              disableUnderline: true,
              inputProps: { "data-qa": "validate-location-filter" }
            }}
            fullWidth
            SelectProps={{
              displayEmpty: true,
              renderValue: value =>
                `${t("validate.validateLocationFilter")}: ${
                  value && validateLocationOptions
                    ? validateLocationOptions.find(
                        option => option.value === value
                      ).label
                    : t("validate.validateFilterShowAllOption")
                }`
            }}
            hiddenLabel
          >
            <MenuItem value="" key="all">
              {t("validate.validateFilterShowAllOption")}
            </MenuItem>
            {!!validateLocationOptions &&
              validateLocationOptions.map(option => (
                <MenuItem value={option.value} key={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            value={shopType}
            onChange={onShopTypeChange}
            variant="filled"
            InputProps={{
              disableUnderline: true,
              inputProps: { "data-qa": "validate-shopType-filter" }
            }}
            fullWidth
            SelectProps={{
              displayEmpty: true,
              renderValue: value =>
                `${t("validate.validateShopTypeFilter")}: ${
                  value && shopTypeOptions
                    ? t(
                        shopTypeOptions.find(option => option.value === value)
                          .label
                      )
                    : t("validate.validateFilterShowAllOption")
                }`
            }}
            hiddenLabel
          >
            <MenuItem value="" key="all">
              {t("validate.validateFilterShowAllOption")}
            </MenuItem>
            {!!shopTypeOptions &&
              shopTypeOptions.map(option => (
                <MenuItem value={option.value} key={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid item xs={12} lg={6} xl={3} md={6}>
          <FilterDatePickerField
            name="createdAfter"
            value={createdAfter}
            maxDate={createdBefore}
            maxDateField={"Created Before"}
            onChange={onCreatedAfterChange}
            label={t("validate.validateFilterCreatedAfterOption")}
            filterErrors={filterErrors}
            setFilterErrors={setFilterErrors}
            classes={classes}
            disabled={createdAtFilterDisabled}
          />
        </Grid>

        <Grid item xs={12} lg={6} xl={3} md={6}>
          <FilterDatePickerField
            name="createdBefore"
            value={createdBefore}
            minDate={createdAfter}
            minDateField={t("validate.validateFilterCreatedAfterOption")}
            onChange={onCreatedBeforeChange}
            label={t("validate.validateFilterCreatedBeforeOption")}
            filterErrors={filterErrors}
            setFilterErrors={setFilterErrors}
            classes={classes}
            disabled={createdAtFilterDisabled}
          />
        </Grid>
        <Grid item xs={12} lg={6} xl={3} md={6}>
          <FilterDatePickerField
            name="programRangeStartedAfter"
            value={programRangeStartedAfter}
            maxDate={programRangeStartedBefore}
            maxDateField={t(
              "validate.validateFilterProgramRangeStartedBeforeOption"
            )}
            onChange={onProgramRangeStartedAfter}
            label={t("validate.validateFilterProgramRangeStartedAfterOption")}
            filterErrors={filterErrors}
            setFilterErrors={setFilterErrors}
            classes={classes}
            disabled={programRangeFilterDisabled}
          />
        </Grid>

        <Grid item xs={12} lg={6} xl={3} md={6}>
          <FilterDatePickerField
            name="programRangeStartedBefore"
            value={programRangeStartedBefore}
            minDate={programRangeStartedAfter}
            minDateField={t(
              "validate.validateFilterProgramRangeStartedAfterOption"
            )}
            onChange={onProgramRangeStartedBefore}
            label={t("validate.validateFilterProgramRangeStartedBeforeOption")}
            filterErrors={filterErrors}
            setFilterErrors={setFilterErrors}
            classes={classes}
            disabled={programRangeFilterDisabled}
          />
        </Grid>
        <Grid item xs={12} style={{ marginTop: "auto" }}>
          <ButtonGroup>
            <Button color="primary" onClick={onClearFilters}>
              {t("validate.validateClearFiltersButton")}
            </Button>

            <Button variant="contained" color="primary" onClick={onFilterList}>
              {t("validate.validateFiltersButton")}
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid>
    </Collapse>
  );
};

const ValidateFilters = ({
  onApplyFilters,
  defaultValues,
  expandedByDefault
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentUser = useUser();
  const { validateLocations, shopTypes } = useSelector(
    state => state.validateState
  );
  const [filterErrors, setFilterErrors] = useState([]);

  const {
    isAdmin,
    isSystemAdmin,
    allowValidateRole,
    isEmployeeManager
  } = usePermissions();

  const locationsOptions =
    validateLocations &&
    validateLocations.map(value => ({ label: value.name, value: value.id }));
  const shopTypesOptions =
    shopTypes &&
    shopTypes.map(value => ({ label: value.description, value: value.id }));
  const [filterMenuExpanded, setFilterMenuExpanded] = useState(
    expandedByDefault
  );
  const [currentFilterInput, setCurrentFilterInput] = useState(defaultValues);

  const createdAtFilterDisabled =
    currentFilterInput["programRangeStartedBefore"] ||
    currentFilterInput["programRangeStartedAfter"];
  const programRangeFilterDisabled =
    currentFilterInput["createdBefore"] || currentFilterInput["createdAfter"];

  useEffect(() => {
    dispatch(fetchShopTypes());
    dispatch(fetchValidateLocations());
  }, []);

  const handleFilterInputChange = field => event => {
    setCurrentFilterInput({
      ...currentFilterInput,
      [field]: event && event.target ? event.target.value : event
    });
  };

  const handleFilterDateChange = field => value => {
    setCurrentFilterInput({
      ...currentFilterInput,
      [field]: value
    });
  };

  const handleSetFilterMenuExpanded = () => {
    setFilterMenuExpanded(!filterMenuExpanded);
  };

  const handleClearFilters = () => {
    setFilterErrors([]);
    onApplyFilters(initialFiltersState);
    setCurrentFilterInput(initialFiltersState);
  };

  const handleFilterList = () => {
    if (filterErrors.length === 0) onApplyFilters({ ...currentFilterInput });
  };

  const handleSearchSubmit = () => {
    onApplyFilters({
      searchInput: currentFilterInput.searchInput
    });
  };

  return (
    <>
      <Grid
        container
        sx={{ my: 2, alignItems: "center", justifyContent: "flex-end" }}
      >
        <Grid item xs={12} md={10}>
          <TextField
            fullWidth
            value={currentFilterInput.searchInput}
            onChange={handleFilterInputChange("searchInput")}
            variant="filled"
            InputProps={{
              disableUnderline: true,
              endAdornment: (
                <Button onClick={handleSearchSubmit}>
                  <Search color="action" />
                </Button>
              ),
              onKeyDown: ({ keyCode }) =>
                keyCode === enterKeyCode && handleSearchSubmit(),
              inputProps: { "data-qa": "validate-search" }
            }}
            placeholder={t("validate.validateSearchPlaceholder")}
            hiddenLabel
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <ButtonGroup>
            <Button
              data-qa="filter-drop-down"
              color="primary"
              endIcon={filterMenuExpanded ? <ArrowDropUp /> : <ArrowDropDown />}
              onClick={handleSetFilterMenuExpanded}
            >
              {t("validate.validateFiltersDropdown")}
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid>
      <ExpandableFilterMenu
        expanded={filterMenuExpanded}
        statusValue={currentFilterInput.statusValue}
        location={currentFilterInput.location}
        shopType={currentFilterInput.shopType}
        createdAfter={currentFilterInput.createdAfter}
        createdBefore={currentFilterInput.createdBefore}
        programRangeStartedAfter={currentFilterInput.programRangeStartedAfter}
        programRangeStartedBefore={currentFilterInput.programRangeStartedBefore}
        validateLocationOptions={locationsOptions}
        filterErrors={filterErrors}
        setFilterErrors={setFilterErrors}
        createdAtFilterDisabled={createdAtFilterDisabled}
        programRangeFilterDisabled={programRangeFilterDisabled}
        statusOptions={
          isAdmin ||
          isSystemAdmin ||
          ((isEmployeeManager || allowValidateRole) &&
            currentUser.isGroupLeader)
            ? validateStatuses
            : validateStatuses.filter(
                status =>
                  status.value === "ready_for_review" ||
                  status.value === "completed" ||
                  status.value === "incomplete" ||
                  status.value === "ready_for_supervisor_review"
              )
        }
        shopTypeOptions={shopTypesOptions}
        onStatusChange={handleFilterInputChange("statusValue")}
        onValidateLocationChange={handleFilterInputChange("location")}
        onShopTypeChange={handleFilterInputChange("shopType")}
        onCreatedAfterChange={handleFilterDateChange("createdAfter")}
        onCreatedBeforeChange={handleFilterDateChange("createdBefore")}
        onProgramRangeStartedAfter={handleFilterDateChange(
          "programRangeStartedAfter"
        )}
        onProgramRangeStartedBefore={handleFilterDateChange(
          "programRangeStartedBefore"
        )}
        onFilterList={handleFilterList}
        onClearFilters={handleClearFilters}
      />
      <Divider />
    </>
  );
};

export default ValidateFilters;

ValidateFilters.propTypes = {
  onApplyFilters: PropTypes.func
};
